import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { ThemeDrawer } from '@theme/theme-drawer';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';
import { StockLimitNotice } from '@theme/stock-limit-notice';
// Registers the <back-in-stock> custom element. The size sheet reuses the
// PDP's own back-in-stock instance (kept in updateQuickAddModal below)
// rather than fetching/rendering a fresh one, and that markup is cloned in
// from a fetched page rather than parsed by the browser, so its own inline
// <script> tag never executes — this static import is what actually loads
// the module on pages that only ever show the sheet (PLP, search). Side
// effect only.
import '@theme/back-in-stock';

// How long the size sheet's tapped row shows its "Added" confirmation
// (SM-679) before the sheet closes — long enough to actually register as
// feedback, short enough not to feel like the tap did nothing.
const ADDED_ROW_DISPLAY_DURATION = 900;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();
    // TWP patch (SM-762): a quick-add outside a product card or Horizon hotspot
    // (the 5 Ways to Wear hotspot card) carries its own product URL.
    const href = productLink?.href || (this.dataset.productUrl ? new URL(this.dataset.productUrl, window.location.origin).href : '');

    if (!href) return '';

    const url = new URL(href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    // A context-bound component always routes through the size sheet, so a
    // swatch selection must not flip it back to a direct add.
    if (this.dataset.quickAddContext === 'complete-the-look') return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const trigger = event.target instanceof Element ? event.target.closest('button') : null;

    const dialogComponent = document.getElementById('quick-add-dialog');
    const dialog = dialogComponent instanceof QuickAddDialog ? dialogComponent.refs?.dialog : null;
    const quickAddContext = this.dataset.quickAddContext || '';
    if (dialog) {
      if (quickAddContext) {
        dialog.dataset.quickAddContext = quickAddContext;
      } else {
        delete dialog.dataset.quickAddContext;
      }
    }

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      if (quickAddContext !== 'complete-the-look') this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal(trigger);
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  /** @param {HTMLButtonElement | null} trigger */
  #openQuickAddModal = (trigger) => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    if (trigger) {
      dialogComponent.addEventListener(DialogCloseEvent.eventName, () => trigger.focus({ preventScroll: true }), {
        once: true,
      });
    }

    // In the size-sheet context showModal()'s auto-focus lands on the close
    // button and paints its focus ring on every open — the design shows no
    // ring at rest. Point the native autofocus at the sheet container
    // BEFORE the dialog opens so focus starts there instead; keyboard users
    // still Tab straight to the rows and close, and closing returns focus
    // to the card's plus via the DialogCloseEvent above.
    const preOpenDialog = dialogComponent.refs?.dialog;
    const sheetContent = preOpenDialog?.querySelector('.quick-add-modal__content');
    if (preOpenDialog?.dataset.quickAddContext === 'complete-the-look' && sheetContent instanceof HTMLElement) {
      sheetContent.tabIndex = -1;
      sheetContent.setAttribute('autofocus', '');
    } else if (sheetContent instanceof HTMLElement) {
      sheetContent.removeAttribute('autofocus');
    }

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });

    if (dialog.dataset.quickAddContext === 'complete-the-look') {
      const content = dialog.querySelector('.quick-add-modal__content');
      // Some dialog implementations re-focus their close button after the
      // open animation; assert the sheet's focus target once more then.
      if (content instanceof HTMLElement) {
        requestAnimationFrame(() => {
          if (dialog.open && dialog.contains(document.activeElement) && document.activeElement !== content) {
            content.focus({ preventScroll: true });
          }
        });
      }
    }
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Gives the sheet's copy of the back-in-stock drawer its own id. Every
   * product renders it from the same buy-buttons block, so the copy carries
   * the same id as the drawer already on the page; its close button targets
   * `#<id>/close`, which Horizon resolves with document.querySelector — the
   * page's own (closed) drawer — so the sheet's drawer could not be closed.
   * Every attribute naming the id (the drawer, its heading and labelledby,
   * the close and trigger targets) is rewritten together.
   * @param {Element} backInStock - The back-in-stock element moved into the sheet.
   */
  #isolateBackInStock(backInStock) {
    const drawer = backInStock.querySelector('theme-drawer[id]');
    if (!drawer || drawer.id.endsWith('-quick-add')) return;

    const pageId = drawer.id;
    const sheetId = `${pageId}-quick-add`;
    for (const element of [backInStock, ...backInStock.querySelectorAll('*')]) {
      for (const { name, value } of [...element.attributes]) {
        if (value.includes(pageId)) element.setAttribute(name, value.split(pageId).join(sheetId));
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (this.dataset.quickAddContext === 'complete-the-look') {
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const sizeFieldset = variantPicker?.querySelector('.variant-option:has(.variant-option__legend--size)');
      // Rendered by blocks/buy-buttons.liquid alongside product-form-component
      // on the real PDP for whatever variant happened to be selected there —
      // kept here so a tap on an unavailable size row can repoint its dataset
      // at the tapped variant and open it (#openNotifyForVariant), instead of
      // the sheet needing its own drawer per size.
      const backInStock = productGrid.querySelector('back-in-stock');

      if (variantPicker && sizeFieldset) {
        // The non-size options stay in the DOM (the sheet's CSS hides them):
        // variant-picker reads the whole selection when a size is tapped, and
        // removing them made the option_values request ambiguous — every size
        // came back unresolvable and unavailable after the first selection.
        productGrid.replaceChildren(variantPicker);
        if (productFormComponent) productGrid.appendChild(productFormComponent);
        if (backInStock) {
          this.#isolateBackInStock(backInStock);
          productGrid.appendChild(backInStock);
        }
      }
    } else if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    // morph() synchronises the fetched product-grid attributes onto the
    // persistent modal host. Restore its stable hook for subsequent card
    // clicks and for context-specific modal styling.
    modalContent.id = 'quick-add-modal-content';
    modalContent.classList.add('quick-add-modal__content');

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  /** Guards the size sheet's tap-to-add against double submission. */
  #addInFlight = false;

  /** The row currently showing a busy/adding state, if any. */
  #busyLabel = /** @type {Element | null} */ (null);

  /** The row currently showing its post-add "Added" confirmation, if any. */
  #addedLabel = /** @type {Element | null} */ (null);

  /** Timer that closes the sheet once the "Added" confirmation has been visible long enough. */
  #addedTimeoutId = /** @type {number | null} */ (null);

  /**
   * True while a size-sheet tap's own variant-fetch confirmation
   * (#handleProductSelect's event.promise) hasn't resolved yet. A repeat tap
   * on that same row sets data-current-checked synchronously, before its
   * fetch confirms anything — without this guard, #handleSizeSheetClick's
   * "already selected, add directly" fast path would submit that unconfirmed
   * variant immediately, and the resulting #addInFlight lock would then
   * silently swallow a later tap on a genuinely different size.
   */
  #confirmationPending = false;

  /**
   * Bumped every time a size-sheet tap dispatches a new variant-select fetch,
   * so only the most recently dispatched one may resolve #confirmationPending
   * or add to cart — an aborted earlier fetch's rejection (or a stray late
   * resolution) must not resurrect a selection the shopper has moved on from.
   */
  #selectionGeneration = 0;

  /** Explains a size-sheet add refused for stock — the sheet hides the form's own error (SM-750). */
  #stockNotice = new StockLimitNotice(this);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener('click', this.#handleSizeSheetClick);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    // #setRowAdded arms a live setTimeout that calls closeDialog() — left
    // running, it would still fire on a disconnected instance.
    this.#clearRowAdded();
    this.#stockNotice.hide();
    this.removeEventListener('click', this.#handleSizeSheetClick);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Whether the dialog is showing as the size bottom sheet, where the rows
   * are the action — selecting an available size adds it to the cart.
   * The open check matters: updateQuickAddModal syncs the card's selection
   * into the modal before the dialog shows, and that synthetic change must
   * not add anything.
   */
  get #isSizeSheet() {
    const dialog = this.refs?.dialog;
    return dialog?.dataset.quickAddContext === 'complete-the-look' && dialog.open === true;
  }

  /**
   * Adds a size-sheet selection to the cart through the hidden product
   * form's own submit path, so cart events, drawer updates, fly-to-cart and
   * error handling all behave exactly as the form's buy button would.
   * @param {{id?: number | string, available?: boolean} | null | undefined} resource - The selected variant.
   */
  #addSizeSheetSelection(resource) {
    if (!resource?.available || !resource.id) {
      this.#clearRowBusy();
      return;
    }
    if (this.#addInFlight) return;

    const form = this.querySelector('product-form-component form');
    const variantInput = form?.querySelector('input[name="id"]');
    if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement)) {
      this.#clearRowBusy();
      return;
    }

    this.#addInFlight = true;
    this.#stockNotice.hide();
    // The rendered input can arrive disabled when the render's default
    // variant was unavailable; the selection being added is available.
    variantInput.disabled = false;
    variantInput.value = String(resource.id);
    form.requestSubmit();
  }

  /**
   * Opens the notify-me panel for a tapped unavailable size row.
   *
   * Reuses the single back-in-stock instance updateQuickAddModal kept in the
   * sheet (rendered by blocks/buy-buttons.liquid for whichever variant was
   * selected on the real PDP) rather than one drawer per size: its dataset
   * is repointed at the tapped variant right before opening, since
   * assets/back-in-stock.js reads `this.dataset.*` fresh at submit time.
   *
   * @param {HTMLInputElement} input - The tapped size row's radio input.
   */
  #openNotifyForVariant(input) {
    const backInStock = this.querySelector('back-in-stock');
    const drawer = backInStock?.querySelector('theme-drawer');
    if (!backInStock || !(drawer instanceof ThemeDrawer)) return;

    const variantId = input.dataset.variantId;
    if (!variantId) return;

    backInStock.dataset.active = 'true';
    backInStock.dataset.variantId = variantId;
    backInStock.dataset.variantName = input.value;
    if (input.dataset.variantSku) backInStock.dataset.variantSku = input.dataset.variantSku;
    if (input.dataset.variantPrice) backInStock.dataset.price = input.dataset.variantPrice;

    const priceDisplay = backInStock.querySelector('[data-back-in-stock-price]');
    if (priceDisplay && input.dataset.variantPriceFormatted) {
      priceDisplay.textContent = input.dataset.variantPriceFormatted;
    }
    const nameDisplay = backInStock.querySelector('[data-back-in-stock-variant-name]');
    if (nameDisplay) nameDisplay.textContent = input.value;

    drawer.open();
  }

  /**
   * Marks a size-sheet row as busy while its add is pending, clearing any
   * previously-busy row first (only one add can be in flight at a time).
   * @param {Element} label - The row's label element.
   */
  #setRowBusy(label) {
    this.#clearRowBusy();
    // A tap on another row while one is still showing its "Added"
    // confirmation supersedes that confirmation's pending auto-close —
    // otherwise its timer could close the sheet out from under this new add.
    this.#clearRowAdded();
    this.#busyLabel = label;
    label.setAttribute('aria-busy', 'true');
  }

  #clearRowBusy() {
    this.#busyLabel?.removeAttribute('aria-busy');
    this.#busyLabel = null;
  }

  /**
   * Marks a size-sheet row as just added, then closes the sheet after it has
   * been visible for ADDED_ROW_DISPLAY_DURATION — without this pause the
   * sheet closed the instant the cart updated and the confirmation never had
   * a chance to render (SM-679).
   * @param {Element} label - The row's label element.
   */
  #setRowAdded(label) {
    this.#clearRowAdded();
    this.#addedLabel = label;
    label.setAttribute('data-added', 'true');
    this.#addedTimeoutId = window.setTimeout(() => {
      this.#addedTimeoutId = null;
      this.closeDialog();
    }, ADDED_ROW_DISPLAY_DURATION);
  }

  #clearRowAdded() {
    if (this.#addedTimeoutId !== null) {
      clearTimeout(this.#addedTimeoutId);
      this.#addedTimeoutId = null;
    }
    this.#addedLabel?.removeAttribute('data-added');
    this.#addedLabel = null;
  }

  /**
   * On a successful size-sheet add, shows the tapped row's "Added"
   * confirmation and closes the dialog once it's had time to be seen;
   * every other context (and any error) keeps closing immediately, as before.
   *
   * The target row is read synchronously, here, rather than inside the
   * .then() below: product-form.js dispatches this event (and this listener
   * runs) synchronously at submit time, before its fetch even starts, so
   * `this.#busyLabel` at this point is guaranteed to be the row THIS request
   * was submitted for. Reading it inside the .then() instead would reflect
   * whatever the sheet happens to be doing whenever this particular
   * request's promise settles — which, if the sheet was closed and reopened
   * for a different, later add while this one was still in flight, could by
   * then be a completely unrelated row.
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    const addedLabel = this.#isSizeSheet ? this.#busyLabel : null;
    const isSizeSheet = this.#isSizeSheet;

    event.promise
      ?.then(({ detail }) => {
        this.#addInFlight = false;
        if (detail?.didError) {
          this.#clearRowBusy();
          const variantInput = this.querySelector('product-form-component form input[name="id"]');
          if (isSizeSheet && variantInput instanceof HTMLInputElement) {
            this.#stockNotice.show(detail.items, Number(variantInput.value), addedLabel);
          }
          return;
        }

        this.#clearRowBusy();

        if (addedLabel) {
          this.#setRowAdded(addedLabel);
        } else {
          this.closeDialog();
        }
      })
      .catch((error) => {
        this.#addInFlight = false;
        this.#clearRowBusy();
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Captured at dispatch time: the select event fires synchronously with
    // the change, so this reflects the state the shopper acted in.
    const sizeSheetSelection = this.#isSizeSheet;
    const generation = sizeSheetSelection ? ++this.#selectionGeneration : this.#selectionGeneration;
    if (sizeSheetSelection) this.#confirmationPending = true;

    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        // A later tap superseded this one (its own fetch aborted this one's,
        // see fetchUpdatedSection) — don't let a straggling resolution add it.
        if (sizeSheetSelection && generation === this.#selectionGeneration) {
          this.#confirmationPending = false;
          this.#addSizeSheetSelection(detail?.resource);
        }

        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (sizeSheetSelection && generation === this.#selectionGeneration) this.#confirmationPending = false;
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /**
   * In the size sheet the row is the action: a tap on an available size adds
   * that variant straight away, off the row's own variant id. It does not
   * wait for the variant-picker's section fetch (the productSelect path
   * above) — that fetch is slow enough that a second tap could land first
   * and the earlier selection would be the one added. The already-selected
   * row fires no change event at all, so this is also the only add path for
   * a repeat tap.
   * @param {MouseEvent} event - The click event.
   */
  #handleSizeSheetClick = (event) => {
    if (!this.#isSizeSheet || !(event.target instanceof Element)) return;

    const label = event.target.closest('.variant-option__button-label');
    // Only the size fieldset's rows are the visible sheet — the other option
    // fieldsets are hidden but still present for variant resolution.
    if (!label?.closest('.variant-option')?.querySelector('.variant-option__legend--size')) return;

    const input = label.querySelector('input[type="radio"]');
    if (!(input instanceof HTMLInputElement)) return;

    // Clicking a <label> wrapping a radio fires TWO click events that bubble
    // here: one targeting the label itself, then one the browser forwards to
    // the wrapped input as part of its native activation behaviour. Acting on
    // both double-submits every tap — only the input-targeted one is the
    // canonical, exactly-once activation event, regardless of whether the
    // shopper tapped the label, a child span, or the input directly.
    if (event.target !== input) return;

    // Sold-out, notifyable rows (variant-option__button-label--back-in-stock
    // in snippets/variant-main-picker.liquid) stay tappable in the sheet —
    // unlike the compact PDP chip, they must open the notify-me panel for
    // that variant rather than doing nothing. Caught before the browser
    // checks the radio, so the row never becomes the "selected" size.
    if (input.dataset.optionAvailable === 'false') {
      event.preventDefault();
      this.#openNotifyForVariant(input);
      return;
    }

    // This row is already showing its "Added" confirmation (SM-679) — the
    // sheet is about to auto-close on its own, so a stray tap in that brief
    // window must not resubmit the same variant a second time.
    if (label.dataset.added === 'true') return;

    // Every tap on an available row leads to either an immediate add (below)
    // or a deferred one once #handleProductSelect's variant fetch resolves —
    // show the row as busy now rather than leaving the tap looking ignored.
    this.#setRowBusy(label);

    if (!input.checked) return;
    if (input.dataset.currentChecked !== 'true') return;
    // This row's own selection may still be awaiting fetchUpdatedSection's
    // confirmation (see #confirmationPending) — data-current-checked flips
    // synchronously well before that resolves, so a repeat tap here would
    // otherwise add an unconfirmed variant and starve a later, different tap.
    if (this.#confirmationPending) return;
    if (!input.dataset.variantId) {
      this.#clearRowBusy();
      return;
    }

    this.#addSizeSheetSelection({ id: input.dataset.variantId, available: true });
  };

  #handleDialogClose = () => {
    // A failed submission must not leave the sheet dead on its next open.
    this.#addInFlight = false;
    this.#confirmationPending = false;
    this.#clearRowBusy();
    this.#clearRowAdded();
    this.#stockNotice.hide();
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
